import { useState } from 'react'
import { Link } from 'react-router-dom'
import BookingModal from '../components/BookingModal'
import FontAwesomeIcon from '../components/FontAwesomeIcon'

export default function KibaleNationalPark() {
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [selectedExperience, setSelectedExperience] = useState('')

  const handleBooking = (experience) => {
    setSelectedExperience(experience)
    setIsModalOpen(true)
  }

  return (
    <div>
      {/* Hero Section */}
      <section
        className="relative h-80 md:h-96 bg-cover bg-center flex items-center"
        style={{
          backgroundImage: 'linear-gradient(rgba(0,0,0,0.5), rgba(0,0,0,0.5)), url(/images/kibale-hero.jpg)',
        }}
      >
        <div className="container mx-auto px-4 text-center text-white">
          <h1 className="text-3xl md:text-5xl font-bold mb-3 font-heading">
            Kibale National Park
          </h1>
          <p className="text-base md:text-lg max-w-2xl mx-auto">
            The Primate Capital of the World
          </p>
        </div>
      </section>

      {/* Overview */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4 max-w-6xl">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-2xl font-bold text-safari-green mb-4">Destination Overview</h2>
              <p className="text-lg text-gray-700 leading-relaxed mb-6">
                Kibale National Park is a protected tropical rainforest located in western Uganda, approximately 358
                kilometers from Kampala and just 26 kilometers southeast of the picturesque town of Fort Portal. The
                park spans 795 square kilometers and occupies an elevated position on the Fort Portal plateau, with
                altitudes ranging from 1,100 to 1,600 meters above sea level.
              </p>
              <p className="text-lg text-gray-700 leading-relaxed mb-6">
                Renowned worldwide as the "Primate Capital of the World," Kibale protects one of Africa's most
                impressive concentrations of primates. The park is home to 1,450 chimpanzees—our closest living
                relatives—and an incredible 13 primate species in total. Beyond primates, Kibale's lush rainforest
                ecosystem harbors 375 bird species, 250 butterfly species, and a rich diversity of flora making it
                one of East Africa's most biodiverse protected areas.
              </p>
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h3 className="font-bold text-safari-green mb-2">Size</h3>
                  <p className="text-gray-700">795 km²</p>
                </div>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h3 className="font-bold text-safari-green mb-2">Altitude</h3>
                  <p className="text-gray-700">1,100-1,600m</p>
                </div>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h3 className="font-bold text-safari-green mb-2">Primate Species</h3>
                  <p className="text-gray-700">13 species</p>
                </div>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h3 className="font-bold text-safari-green mb-2">Chimpanzees</h3>
                  <p className="text-gray-700">1,450 individuals</p>
                </div>
              </div>
            </div>
            <div>
              <img
                src="/images/kibale-hero.jpg"
                alt="Kibale National Park"
                className="w-full h-96 object-cover rounded-lg shadow-lg"
              />
            </div>
          </div>
        </div>
      </section>

      {/* How to Get There */}
      <section className="py-16 bg-safari-light">
        <div className="container mx-auto px-4 max-w-6xl">
          <h2 className="text-2xl font-bold text-safari-green mb-8 text-center">How to Get There</h2>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="card">
              <div className="p-6">
                <h3 className="text-xl font-bold text-safari-green mb-4 flex items-center gap-2">
                  <FontAwesomeIcon icon="fa-car" className="w-6 h-6" />
                  By Road from Kampala
                </h3>
                <div className="space-y-3 text-gray-700">
                  <p><strong>Distance:</strong> 358 km via Fort Portal route</p>
                  <p><strong>Travel Time:</strong> 5-6 hours through scenic western Uganda countryside</p>
                  <p><strong>Route:</strong> Kampala → Mubende → Fort Portal → Kibale (well-maintained tarmac)</p>
                  <p className="text-sm italic">Alternative route via Mbarara and Queen Elizabeth National Park (7-8 hours). Ideal for combined safaris.</p>
                </div>
              </div>
            </div>
            <div className="card">
              <div className="p-6">
                <h3 className="text-xl font-bold text-safari-green mb-4 flex items-center gap-2">
                  <FontAwesomeIcon icon="fa-plane" className="w-6 h-6" />
                  By Air
                </h3>
                <div className="space-y-3 text-gray-700">
                  <p><strong>Kasese Airfield:</strong> Charter flights from Entebbe (1 hour flight). Located 2-3 hours drive from Kibale.</p>
                  <p><strong>Best For:</strong> Time-conscious travelers or those combining with Rwenzori Mountains or Queen Elizabeth NP.</p>
                  <p className="text-sm italic">Charter flights can be arranged through aviation operators. Road transfer from airfield to park included in tour packages.</p>
                </div>
              </div>
            </div>
            <div className="card">
              <div className="p-6">
                <h3 className="text-xl font-bold text-safari-green mb-4 flex items-center gap-2">
                  <FontAwesomeIcon icon="fa-route" className="w-6 h-6" />
                  From Other Parks
                </h3>
                <div className="space-y-3 text-gray-700">
                  <p><strong>Queen Elizabeth NP:</strong> 2-3 hours drive through beautiful countryside and crater lake region.</p>
                  <p><strong>Semuliki Valley NP:</strong> 1.5-2 hours drive from Fort Portal area.</p>
                  <p><strong>Bwindi Forest:</strong> 6-7 hours via Mbarara. Perfect for primate-focused itineraries.</p>
                  <p className="text-sm italic">Kibale combines perfectly with western Uganda circuit safaris.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Key Highlights */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4 max-w-6xl">
          <h2 className="text-2xl font-bold text-safari-green mb-8 text-center">Key Highlights</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="card group">
              <div className="relative h-32 overflow-hidden flex-shrink-0 bg-gradient-to-br from-safari-green to-safari-gold flex items-center justify-center">
                <FontAwesomeIcon icon="fa-paw" className="w-16 h-16 text-white" />
              </div>
              <div className="p-6">
                <h3 className="text-lg font-bold text-safari-green mb-2">Primate Capital of the World</h3>
                <p className="text-gray-700">
                  With 13 primate species including 1,450 chimpanzees, Kibale offers the highest primate density in Africa. Red colobus, L'Hoest's monkey, blue monkey, grey-cheeked mangabey, red-tailed monkey, and black-and-white colobus thrive here.
                </p>
              </div>
            </div>
            <div className="card group">
              <div className="relative h-32 overflow-hidden flex-shrink-0 bg-gradient-to-br from-safari-green to-safari-gold flex items-center justify-center">
                <FontAwesomeIcon icon="fa-binoculars" className="w-16 h-16 text-white" />
              </div>
              <div className="p-6">
                <h3 className="text-lg font-bold text-safari-green mb-2">Exceptional Chimpanzee Trekking</h3>
                <p className="text-gray-700">
                  Kibale offers the world's best chimpanzee trekking with 90% success rates. Habituated communities allow close encounters with these intelligent primates in their natural forest habitat.
                </p>
              </div>
            </div>
            <div className="card group">
              <div className="relative h-32 overflow-hidden flex-shrink-0 bg-gradient-to-br from-safari-green to-safari-gold flex items-center justify-center">
                <FontAwesomeIcon icon="fa-water" className="w-16 h-16 text-white" />
              </div>
              <div className="p-6">
                <h3 className="text-lg font-bold text-safari-green mb-2">Bigodi Wetland Sanctuary</h3>
                <p className="text-gray-700">
                  This community-managed 4km boardwalk trail showcases 8 primate species and 138 bird species. A model ecotourism project directly benefiting local communities through sustainable tourism.
                </p>
              </div>
            </div>
            <div className="card group">
              <div className="relative h-32 overflow-hidden flex-shrink-0 bg-gradient-to-br from-safari-green to-safari-gold flex items-center justify-center">
                <FontAwesomeIcon icon="fa-mountain-sun" className="w-16 h-16 text-white" />
              </div>
              <div className="p-6">
                <h3 className="text-lg font-bold text-safari-green mb-2">Crater Lakes Region</h3>
                <p className="text-gray-700">
                  Dozens of picturesque volcanic crater lakes dot the landscape between Kibale and Fort Portal. These stunning turquoise and emerald pools create spectacular photo opportunities and hiking destinations.
                </p>
              </div>
            </div>
            <div className="card group">
              <div className="relative h-32 overflow-hidden flex-shrink-0 bg-gradient-to-br from-safari-green to-safari-gold flex items-center justify-center">
                <FontAwesomeIcon icon="fa-dove" className="w-16 h-16 text-white" />
              </div>
              <div className="p-6">
                <h3 className="text-lg font-bold text-safari-green mb-2">Premier Birding Destination</h3>
                <p className="text-gray-700">
                  375 bird species including African grey parrot, green-breasted pitta, and 6 Albertine Rift endemics. Exceptional diversity from forest canopy to wetland habitats makes Kibale a birder's paradise.
                </p>
              </div>
            </div>
            <div className="card group">
              <div className="relative h-32 overflow-hidden flex-shrink-0 bg-gradient-to-br from-safari-green to-safari-gold flex items-center justify-center">
                <FontAwesomeIcon icon="fa-tree" className="w-16 h-16 text-white" />
              </div>
              <div className="p-6">
                <h3 className="text-lg font-bold text-safari-green mb-2">Ancient Tropical Rainforest</h3>
                <p className="text-gray-700">
                  Pristine mid-altitude moist evergreen and semi-deciduous forest creates a cathedral-like canopy. Giant forest trees tower over 55 meters, supporting complex ecosystems and incredible biodiversity.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Activities & Experiences */}
      <section className="py-16 bg-safari-light">
        <div className="container mx-auto px-4 max-w-6xl">
          <h2 className="text-2xl font-bold text-safari-green mb-8 text-center">Activities & Experiences</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="card group">
              <div className="relative h-40 overflow-hidden flex-shrink-0 bg-gradient-to-br from-safari-green to-safari-gold flex items-center justify-center">
                <FontAwesomeIcon icon="fa-paw" className="w-20 h-20 text-white" />
              </div>
              <div className="p-6 flex flex-col flex-grow">
                <h3 className="text-lg font-bold text-safari-green mb-2">Chimpanzee Trekking</h3>
                <p className="text-gray-700 mb-4 flex-grow">
                  Track habituated chimpanzees through pristine rainforest with expert guides. High success rate (90%+) for sightings. Permits required—book in advance.
                </p>
                <div className="space-y-2 mb-4 text-sm text-gray-600">
                  <p><FontAwesomeIcon icon="fa-clock" className="w-4 h-4 mr-2" />Duration: 2-5 hours</p>
                  <p><FontAwesomeIcon icon="fa-chart-simple" className="w-4 h-4 mr-2" />Difficulty: Moderate</p>
                </div>
                <p className="text-safari-gold font-bold mb-3 text-center text-lg">From $200 per person</p>
                <Link to="/activities/chimpanzee-trekking" className="btn-primary w-full text-center block">
                  View Experience
                </Link>
              </div>
            </div>
            <div className="card group">
              <div className="relative h-40 overflow-hidden flex-shrink-0 bg-gradient-to-br from-safari-green to-safari-gold flex items-center justify-center">
                <FontAwesomeIcon icon="fa-user-astronaut" className="w-20 h-20 text-white" />
              </div>
              <div className="p-6 flex flex-col flex-grow">
                <h3 className="text-lg font-bold text-safari-green mb-2">Chimpanzee Habituation Experience</h3>
                <p className="text-gray-700 mb-4 flex-grow">
                  Spend an entire day with semi-habituated chimp groups alongside researchers. A rare research-like immersion into primate behavior and forest ecology.
                </p>
                <div className="space-y-2 mb-4 text-sm text-gray-600">
                  <p><FontAwesomeIcon icon="fa-clock" className="w-4 h-4 mr-2" />Duration: Full day (dawn to dusk)</p>
                  <p><FontAwesomeIcon icon="fa-chart-simple" className="w-4 h-4 mr-2" />Difficulty: Moderate to Strenuous</p>
                </div>
                <p className="text-safari-gold font-bold mb-3 text-center text-lg">Permit fees apply</p>
                <Link to="/activities/chimp-habituation" className="btn-primary w-full block text-center">
                  View Details
                </Link>
              </div>
            </div>
            <div className="card group">
              <div className="relative h-40 overflow-hidden flex-shrink-0 bg-gradient-to-br from-safari-green to-safari-gold flex items-center justify-center">
                <FontAwesomeIcon icon="fa-moon" className="w-20 h-20 text-white" />
              </div>
              <div className="p-6 flex flex-col flex-grow">
                <h3 className="text-lg font-bold text-safari-green mb-2">Night Forest Walks</h3>
                <p className="text-gray-700 mb-4 flex-grow">
                  Discover the nocturnal forest with spotlight walks. Spot bushbabies, pottos, and glowing bioluminescent fungi in this magical after-dark experience.
                </p>
                <div className="space-y-2 mb-4 text-sm text-gray-600">
                  <p><FontAwesomeIcon icon="fa-clock" className="w-4 h-4 mr-2" />Duration: 2-3 hours</p>
                  <p><FontAwesomeIcon icon="fa-chart-simple" className="w-4 h-4 mr-2" />Difficulty: Moderate</p>
                </div>
                <p className="text-safari-gold font-bold mb-3 text-center text-lg">From $40 per person</p>
                <button onClick={() => handleBooking('Night Forest Walks - Kibale')} className="btn-primary w-full">
                  Book Experience
                </button>
              </div>
            </div>
            <div className="card group">
              <div className="relative h-40 overflow-hidden flex-shrink-0 bg-gradient-to-br from-safari-green to-safari-gold flex items-center justify-center">
                <FontAwesomeIcon icon="fa-water" className="w-20 h-20 text-white" />
              </div>
              <div className="p-6 flex flex-col flex-grow">
                <h3 className="text-lg font-bold text-safari-green mb-2">Bigodi Wetland Sanctuary Walk</h3>
                <p className="text-gray-700 mb-4 flex-grow">
                  Community-led boardwalk through lush swamps teeming with primates, colorful birds, and butterflies. Supports local conservation and livelihoods.
                </p>
                <div className="space-y-2 mb-4 text-sm text-gray-600">
                  <p><FontAwesomeIcon icon="fa-clock" className="w-4 h-4 mr-2" />Duration: 2-3 hours</p>
                  <p><FontAwesomeIcon icon="fa-chart-simple" className="w-4 h-4 mr-2" />Difficulty: Easy</p>
                </div>
                <p className="text-safari-gold font-bold mb-3 text-center text-lg">From $30 per person</p>
                <button onClick={() => handleBooking('Bigodi Wetland - Kibale')} className="btn-primary w-full">
                  Book Experience
                </button>
              </div>
            </div>
            <div className="card group">
              <div className="relative h-40 overflow-hidden flex-shrink-0 bg-gradient-to-br from-safari-green to-safari-gold flex items-center justify-center">
                <FontAwesomeIcon icon="fa-mountain" className="w-20 h-20 text-white" />
              </div>
              <div className="p-6 flex flex-col flex-grow">
                <h3 className="text-lg font-bold text-safari-green mb-2">Crater Lake Hikes</h3>
                <p className="text-gray-700 mb-4 flex-grow">
                  Trek around dramatic volcanic crater lakes with panoramic views of the Rwenzori Mountains. Perfect for photography and connecting with nature.
                </p>
                <div className="space-y-2 mb-4 text-sm text-gray-600">
                  <p><FontAwesomeIcon icon="fa-clock" className="w-4 h-4 mr-2" />Duration: 3-5 hours</p>
                  <p><FontAwesomeIcon icon="fa-chart-simple" className="w-4 h-4 mr-2" />Difficulty: Moderate</p>
                </div>
                <p className="text-safari-gold font-bold mb-3 text-center text-lg">From $35 per person</p>
                <Link to="/activities/crater-lakes-drive" className="btn-primary w-full text-center block">
                  View Experience
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Travel Tips */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4 max-w-6xl">
          <h2 className="text-2xl font-bold text-safari-green mb-8 text-center">Travel Tips</h2>
          <div className="grid md:grid-cols-2 gap-8">
            <div className="card">
              <div className="p-6">
                <h3 className="text-xl font-bold text-safari-green mb-4 flex items-center gap-2">
                  <FontAwesomeIcon icon="fa-calendar" className="w-6 h-6" />
                  Best Time to Visit
                </h3>
                <div className="space-y-3 text-gray-700">
                  <p><strong>Dry Seasons (December-February, June-September):</strong> Optimal trekking conditions with less muddy trails and easier forest navigation. Peak season for chimp tracking.</p>
                  <p><strong>Wet Seasons (March-May, October-November):</strong> Lush forest scenery with abundant fruit attracting chimps. Fewer visitors mean more exclusive experiences despite occasional rain.</p>
                  <p className="text-sm italic">Chimpanzee tracking available year-round. Book permits 2-3 months in advance for dry season.</p>
                </div>
              </div>
            </div>
            <div className="card">
              <div className="p-6">
                <h3 className="text-xl font-bold text-safari-green mb-4 flex items-center gap-2">
                  <FontAwesomeIcon icon="fa-suitcase" className="w-6 h-6" />
                  What to Pack
                </h3>
                <div className="space-y-2 text-gray-700">
                  <p><strong>Essential:</strong> Sturdy hiking boots, rain jacket, long pants and sleeves (protection from nettles), gardening gloves</p>
                  <p><strong>Recommended:</strong> Daypack, insect repellent, sunscreen, hat, reusable water bottle, binoculars</p>
                  <p><strong>Camera Gear:</strong> No flash allowed near primates. Bring extra batteries and memory cards</p>
                  <p className="text-sm italic">Dress in neutral earth-tone colors. Porters available to assist on treks for small fee.</p>
                </div>
              </div>
            </div>
            <div className="card">
              <div className="p-6">
                <h3 className="text-xl font-bold text-safari-green mb-4 flex items-center gap-2">
                  <FontAwesomeIcon icon="fa-heartbeat" className="w-6 h-6" />
                  Health & Safety
                </h3>
                <div className="space-y-2 text-gray-700">
                  <p><strong>Fitness Level:</strong> Moderate fitness required for chimp trekking. Treks can last 2-5 hours over uneven forest terrain.</p>
                  <p><strong>Vaccinations:</strong> Yellow fever certificate required. Malaria prophylaxis recommended for tropical lowlands.</p>
                  <p><strong>Age Limit:</strong> Minimum age 12 years for chimpanzee trekking and habituation.</p>
                  <p><strong>Health Screening:</strong> Anyone with contagious illness will not be permitted to trek (protects primates from human diseases).</p>
                </div>
              </div>
            </div>
            <div className="card">
              <div className="p-6">
                <h3 className="text-xl font-bold text-safari-green mb-4 flex items-center gap-2">
                  <FontAwesomeIcon icon="fa-coins" className="w-6 h-6" />
                  Budgeting Tips
                </h3>
                <div className="space-y-2 text-gray-700">
                  <p><strong>Daily Budget:</strong> Mid-range travelers should budget $150-250 per day including accommodation, permits, meals, and activities.</p>
                  <p><strong>Permits:</strong> Chimpanzee trekking permits and habituation must be booked in advance through Uganda Wildlife Authority or tour operators.</p>
                  <p><strong>Accommodation:</strong> Wide range from budget guesthouses to luxury lodges. Book early for dry season.</p>
                  <p><strong>Tipping:</strong> Budget for guide and porter tips (typically $5-10 per person for half-day activities).</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Where to Stay */}
      <section className="py-16 bg-safari-light">
        <div className="container mx-auto px-4 max-w-6xl">
          <h2 className="text-2xl font-bold text-safari-green mb-6 text-center">Where to Stay</h2>
          <p className="text-center text-gray-700 mb-12 max-w-3xl mx-auto">
            Accommodation options near Kibale range from budget guesthouses to luxury forest lodges, many within walking distance of park headquarters.
          </p>
          <div className="overflow-x-auto">
            <table className="w-full bg-white rounded-lg shadow-lg">
              <thead className="bg-safari-green text-white">
                <tr>
                  <th className="px-6 py-4 text-left">Category</th>
                  <th className="px-6 py-4 text-left">Accommodation Name</th>
                  <th className="px-6 py-4 text-left">Location</th>
                  <th className="px-6 py-4 text-left">Features</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                <tr className="hover:bg-gray-50">
                  <td className="px-6 py-4 font-bold text-safari-green">Luxury</td>
                  <td className="px-6 py-4">Papaya Lake Lodge</td>
                  <td className="px-6 py-4">Crater lake setting</td>
                  <td className="px-6 py-4">Luxury cottages, infinity pool, gourmet dining, stunning views</td>
                </tr>
                <tr className="hover:bg-gray-50">
                  <td className="px-6 py-4 font-bold text-safari-green">Luxury</td>
                  <td className="px-6 py-4">Kyaninga Lodge</td>
                  <td className="px-6 py-4">Crater lake shore</td>
                  <td className="px-6 py-4">Exclusive suites, spa, panoramic views, fine dining, premium service</td>
                </tr>
                <tr className="hover:bg-gray-50">
                  <td className="px-6 py-4 font-bold text-safari-green">Mid-Range</td>
                  <td className="px-6 py-4">Primate Lodge</td>
                  <td className="px-6 py-4">Inside Kibale Forest</td>
                  <td className="px-6 py-4">Forest cottages, restaurant, bar, immediate park access</td>
                </tr>
                <tr className="hover:bg-gray-50">
                  <td className="px-6 py-4 font-bold text-safari-green">Mid-Range</td>
                  <td className="px-6 py-4">Kibale Guest Cottages</td>
                  <td className="px-6 py-4">Near park entrance</td>
                  <td className="px-6 py-4">Comfortable bandas, gardens, good meals, convenient location</td>
                </tr>
                <tr className="hover:bg-gray-50">
                  <td className="px-6 py-4 font-bold text-safari-green">Budget</td>
                  <td className="px-6 py-4">Kibale Forest Camp</td>
                  <td className="px-6 py-4">Near Kanyanchu</td>
                  <td className="px-6 py-4">Simple bandas, camping, budget-friendly, social atmosphere</td>
                </tr>
                <tr className="hover:bg-gray-50">
                  <td className="px-6 py-4 font-bold text-safari-green">Budget</td>
                  <td className="px-6 py-4">Chimpanzee Forest Guesthouse</td>
                  <td className="px-6 py-4">Bigodi village</td>
                  <td className="px-6 py-4">Basic rooms, community-owned, affordable, wetland access</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* FAQs */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4 max-w-6xl">
          <h2 className="text-2xl font-bold text-safari-green mb-8 text-center">Frequently Asked Questions</h2>
          <div className="grid md:grid-cols-2 gap-8">
            <div className="space-y-6">
              <div className="border-l-4 border-safari-gold pl-6">
                <h3 className="text-lg font-bold text-safari-green mb-2">How far in advance should I book chimpanzee permits?</h3>
                <p className="text-gray-700">
                  Book permits 2-3 months in advance for peak dry seasons (June-September, December-February). Last-minute availability possible during wet seasons. Permits are limited to protect chimpanzees.
                </p>
              </div>
              <div className="border-l-4 border-safari-gold pl-6">
                <h3 className="text-lg font-bold text-safari-green mb-2">What are the success rates for seeing chimpanzees?</h3>
                <p className="text-gray-700">
                  Kibale boasts exceptional 90% chimpanzee sighting success rates thanks to habituated communities and expert trackers who monitor daily movements. Kibale offers the best chimp tracking in Africa.
                </p>
              </div>
              <div className="border-l-4 border-safari-gold pl-6">
                <h3 className="text-lg font-bold text-safari-green mb-2">What's the difference between trekking and habituation?</h3>
                <p className="text-gray-700">
                  Chimpanzee trekking allows 1 hour with habituated chimps. Habituation Experience is a full day with semi-habituated groups, offering 4+ hours observation while contributing to research. Habituation permits are more expensive but more immersive.
                </p>
              </div>
              <div className="border-l-4 border-safari-gold pl-6">
                <h3 className="text-lg font-bold text-safari-green mb-2">Can I combine Kibale with Queen Elizabeth National Park?</h3>
                <p className="text-gray-700">
                  Absolutely! Kibale is only 2-3 hours from Queen Elizabeth NP. Most western Uganda itineraries combine primate tracking at Kibale with savanna game drives at Queen Elizabeth for a complete wildlife experience.
                </p>
              </div>
            </div>
            <div className="space-y-6">
              <div className="border-l-4 border-safari-gold pl-6">
                <h3 className="text-lg font-bold text-safari-green mb-2">What other primates can I see besides chimpanzees?</h3>
                <p className="text-gray-700">
                  Kibale's 13 primate species include red colobus, L'Hoest's monkey, blue monkey, grey-cheeked mangabey, red-tailed monkey, black-and-white colobus, olive baboons, and several nocturnal species visible on night walks.
                </p>
              </div>
              <div className="border-l-4 border-safari-gold pl-6">
                <h3 className="text-lg font-bold text-safari-green mb-2">Is Bigodi Wetland Sanctuary worth visiting?</h3>
                <p className="text-gray-700">
                  Definitely! This community-run sanctuary offers excellent primate viewing (8 species) and exceptional birding (138 species). The 4km boardwalk is easy to walk and directly supports local community development.
                </p>
              </div>
              <div className="border-l-4 border-safari-gold pl-6">
                <h3 className="text-lg font-bold text-safari-green mb-2">How long should I spend at Kibale?</h3>
                <p className="text-gray-700">
                  Minimum 2 days recommended: one for chimpanzee trekking and one for Bigodi Wetland or nature walks. Add an extra day for the habituation experience or crater lakes exploration.
                </p>
              </div>
              <div className="border-l-4 border-safari-gold pl-6">
                <h3 className="text-lg font-bold text-safari-green mb-2">What's the best time of day for chimp trekking?</h3>
                <p className="text-gray-700">
                  Morning treks (8am start) offer higher energy chimps and better photography light. Afternoon treks (2pm) can be less crowded and chimps are often feeding. Both sessions have high success rates.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="py-16 bg-safari-green text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Experience the Primate Capital of the World
          </h2>
          <p className="text-xl mb-8 max-w-2xl mx-auto">
            Embark on an unforgettable journey into Kibale's lush rainforest. Track habituated chimpanzees, discover
            13 primate species, and immerse yourself in one of Africa's most biodiverse ecosystems. Our expert guides
            ensure exceptional wildlife encounters while supporting vital conservation efforts.
          </p>
          <button
            onClick={() => handleBooking('Complete Kibale Experience')}
            className="btn-primary text-lg"
          >
            Plan Your Kibale Adventure
          </button>
        </div>
      </section>

      <BookingModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        experience={selectedExperience}
      />
    </div>
  )
}
